import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { sowService } from '../services/sowService';
import { useAuthToken } from './AuthTokenProvider';
import { Images, ChevronLeft, ChevronRight, X, Expand, Info, ChevronDown, ChevronUp } from 'lucide-react';

interface ImageGalleryProps {
  sowId: string;
  className?: string;
  refreshTrigger?: number; // Change this value to trigger a refresh
}

interface SOWImage {
  id: string;
  imageId: string;
  filename: string;
  url: string;
  uploadedAt: string;
  timestamp?: string;
  analysisContext?: string;
  questionnaireContext?: {
    type: string;
    questionId?: string;
  };
  analysis?: {
    description?: string;
    extractedData?: {
      visibleElements?: string[];
      datapoints?: { [key: string]: { value: any; confidence: number } };
    };
    status?: string;
    confidence?: number;
    feedback?: string;
    issues?: string[];
    dataPoints?: any;
  };
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({ sowId, className = '', refreshTrigger }) => {
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();
  const [images, setImages] = useState<SOWImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<SOWImage | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showImageDetails, setShowImageDetails] = useState(false);

  useEffect(() => {
    if (sowId && tokenSet && !tokenLoading) {
      loadImages();
    }
  }, [sowId, tokenSet, tokenLoading, refreshTrigger]);

  const loadImages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the proper SOW images endpoint that returns images with signed URLs
      const response = await sowService.getSOWImages(sowId);
      const images: SOWImage[] = response.images || [];
      
      setImages(images);
    } catch (error) {
      console.error('Error loading images:', error);
      setError('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const openImageModal = (image: SOWImage, index: number) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setShowImageDetails(false);
  };

  const goToPrevious = () => {
    if (images.length > 0) {
      const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
      setCurrentIndex(newIndex);
      setSelectedImage(images[newIndex]);
    }
  };

  const goToNext = () => {
    if (images.length > 0) {
      const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
      setCurrentIndex(newIndex);
      setSelectedImage(images[newIndex]);
    }
  };

  if (loading || tokenLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Images className="h-5 w-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">Uploaded Photos</h3>
          </div>
          <div className="text-center py-8">
            <div className="text-sm text-gray-500">Loading images...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Images className="h-5 w-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">Uploaded Photos</h3>
          </div>
          <div className="text-center py-8">
            <div className="text-sm text-red-500">{error}</div>
            <Button
              onClick={loadImages}
              variant="outline"
              size="sm"
              className="mt-3"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (images.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Images className="h-5 w-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">Uploaded Photos</h3>
          </div>
          <div className="text-center py-8">
            <Images className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <div className="text-sm text-gray-500">No photos uploaded yet</div>
            <div className="text-xs text-gray-400 mt-1">
              Photos will appear here as you upload them during the questionnaire
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Images className="h-5 w-5 text-blue-600" />
              <h3 className="font-medium text-gray-900">Uploaded Photos</h3>
            </div>
            <div className="text-xs text-gray-500">
              {images.length} photo{images.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((image, index) => (
              <div
                key={image.id}
                className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-blue-300 transition-colors"
                onClick={() => openImageModal(image, index)}
              >
                <div className="aspect-square bg-gray-50 flex items-center justify-center">
                  <img
                    src={image.url}
                    alt={image.filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <Expand className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                  <div className="text-xs text-white truncate">
                    {image.questionnaireContext?.questionId === 'kitchen_anchor_photo' ? 'Kitchen Overview' : 
                     image.analysisContext?.includes('Question:') ? 
                     `${image.analysisContext.replace('Question: ', '').replace('_', ' ').replace(/([a-z])([A-Z])/g, '$1 $2')}` : 
                     'Kitchen Photo'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="relative max-w-7xl max-h-[95vh] w-full flex flex-col">
            {/* Close button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={closeImageModal}
              className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white text-gray-600 rounded-full p-2 h-10 w-10"
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Navigation buttons */}
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-600 rounded-full p-2 h-10 w-10"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-600 rounded-full p-2 h-10 w-10"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </>
            )}

            {/* Image */}
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl flex flex-col max-h-full">
              <div className="flex items-center justify-center max-h-[60vh] bg-gray-50 flex-shrink-0">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.filename}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              
              {/* Image info */}
              <div className="p-4 border-t overflow-y-auto flex-1">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {selectedImage.questionnaireContext?.questionId === 'kitchen_anchor_photo' ? 'Kitchen Overview Photo' : 
                       selectedImage.analysisContext?.includes('Question:') ? 
                       `${selectedImage.analysisContext.replace('Question: ', '').replace('_', ' ').replace(/([a-z])([A-Z])/g, '$1 $2')}` : 
                       'Kitchen Photo'}
                    </h4>
                    {selectedImage.analysis?.description && (
                      <p className="text-sm text-gray-600 mt-1">{selectedImage.analysis.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Info button */}
                    {selectedImage.analysis && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowImageDetails(!showImageDetails)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                      >
                        <Info className="h-4 w-4" />
                        <span className="text-sm">Analysis Details</span>
                        {showImageDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    )}
                    {images.length > 1 && (
                      <div className="text-sm text-gray-500">
                        {currentIndex + 1} of {images.length}
                      </div>
                    )}
                  </div>
                </div>
                {(selectedImage.uploadedAt || selectedImage.timestamp) && (
                  <div className="text-xs text-gray-500 mt-2">
                    Uploaded {new Date(selectedImage.uploadedAt || selectedImage.timestamp || '').toLocaleString()}
                  </div>
                )}
                
                {/* Detailed Analysis Section */}
                {showImageDetails && selectedImage.analysis && (
                  <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <h5 className="text-sm font-semibold text-blue-900 mb-4 flex items-center gap-2">
                      <Info className="h-4 w-4" />
                      What We Discovered
                    </h5>
                    
                    {/* Analysis Status - Only show if useful */}
                    {selectedImage.analysis.status && selectedImage.analysis.status !== 'pending' && (
                      <div className="mb-4">
                        <div className={`text-sm px-3 py-2 rounded-lg inline-flex items-center gap-2 ${
                          selectedImage.analysis.status === 'accepted' ? 'bg-green-100 text-green-800' :
                          selectedImage.analysis.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          <span className={`w-2 h-2 rounded-full ${
                            selectedImage.analysis.status === 'accepted' ? 'bg-green-600' :
                            selectedImage.analysis.status === 'rejected' ? 'bg-red-600' :
                            'bg-yellow-600'
                          }`}></span>
                          {selectedImage.analysis.status === 'accepted' ? 'Photo Analysis Complete' :
                           selectedImage.analysis.status === 'rejected' ? 'Photo Needs Review' :
                           'Analysis In Progress'}
                        </div>
                      </div>
                    )}
                    
                    {/* Feedback - Make it more conversational */}
                    {selectedImage.analysis.feedback && (
                      <div className="mb-4 p-3 bg-white rounded-lg border border-blue-100">
                        <div className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                          💡 Our Analysis
                        </div>
                        <div className="text-sm text-gray-700 leading-relaxed">{selectedImage.analysis.feedback}</div>
                      </div>
                    )}
                    
                    {/* Issues - Present as recommendations */}
                    {selectedImage.analysis.issues && selectedImage.analysis.issues.length > 0 && (
                      <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                          ⚠️ Important Considerations
                        </div>
                        <ul className="text-sm text-amber-700 space-y-2">
                          {selectedImage.analysis.issues.map((issue, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-amber-600 mt-0.5 font-medium">•</span>
                              <span className="leading-relaxed">
                                {issue.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Extracted Elements - Present as features */}
                    {selectedImage.analysis.extractedData?.visibleElements && selectedImage.analysis.extractedData.visibleElements.length > 0 && (
                      <div className="mb-4">
                        <div className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
                          🏠 Features Identified
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedImage.analysis.extractedData.visibleElements.map((element, index) => (
                            <span key={index} className="text-xs bg-white text-blue-700 px-3 py-1.5 rounded-full border border-blue-200 font-medium">
                              {element.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Extracted Datapoints - Remove confidence scores */}
                    {((selectedImage.analysis.extractedData?.datapoints && Object.keys(selectedImage.analysis.extractedData.datapoints).length > 0) ||
                      (selectedImage.analysis.dataPoints && Object.keys(selectedImage.analysis.dataPoints).length > 0)) && (
                      <div>
                        <div className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
                          📏 Measurements & Details
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                          <div className="space-y-2">
                            {/* Handle both datapoints and dataPoints structures */}
                            {Object.entries(selectedImage.analysis.extractedData?.datapoints || selectedImage.analysis.dataPoints || {}).map(([key, datapoint], index) => (
                              <div key={index} className="flex justify-between items-center text-sm border-b border-gray-100 pb-2 last:border-0">
                                <span className="text-gray-600 capitalize">
                                  {key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')}
                                </span>
                                <span className="text-gray-900 font-semibold">
                                  {typeof datapoint === 'object' && datapoint && 'value' in datapoint ? (datapoint as any).value : datapoint}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};